import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Mail, Pencil, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { Loading } from "@/components/Loading";
import { BoardRepoProvider } from "@/lib/board/context";
import { useBoard } from "@/lib/board/use-board";
import { positionAt } from "@/lib/board/repo";
import { isArchived, shouldUnarchiveOnMove, UNARCHIVE_PATCH } from "@/lib/board/archive";
import {
  applyArchiveView, applyFilters, cardBelongsTo, clearFilters, clickLabel, clickPerson, isFiltering, labelSlugs,
  parseFilters, personSlugs, setCompare, sortCards, writeFilters, type CardIndex, type FilterState,
} from "@/lib/board/filters";
import { LEGEND } from "@/lib/board/look";
import { assigneeChoices, isOpenToAllStaff, openerIds } from "@/lib/board/open-board";
import { displayName, shortId, type Card, type Label, type Person } from "@/lib/board/types";
import type { LinkableObjective } from "@/lib/priority-link";
import { BoardColumns, type ColumnView } from "@/components/board/BoardColumns";
import { PeopleFilter } from "@/components/board/PeopleFilter";
import { FilterBar } from "@/components/board/FilterBar";
import { MembersPanel } from "@/components/board/MembersPanel";
import { TaskDetailModal } from "@/components/board/TaskDetailModal";
import { BoardEditModal } from "@/components/board/BoardEditModal";
import { ScanInboxModal } from "@/components/board/ScanInboxModal";

/** Route /tasks/:boardId. Board screens read and write through the BoardRepo. */
export function BoardView() {
  return (
    <BoardRepoProvider>
      <BoardScreen />
    </BoardRepoProvider>
  );
}

function BoardScreen() {
  const { boardId } = useParams<{ boardId: string }>();
  const { employee } = useAuth();
  const canManageAccess = usePermission("hq.boards.manage_access");
  return <BoardBody boardId={boardId} employee={employee} canManageAccess={canManageAccess} />;
}

/** Who is looking at the board. Split out so tests can render it without auth. */
export interface BoardViewer { id: string; role: string | null }

export function BoardBody({ boardId, employee, canManageAccess }: {
  boardId: string | undefined;
  employee: BoardViewer | null | undefined;
  canManageAccess: boolean;
}) {
  const { snap, setSnap, error, setError, loading, reload, repo } = useBoard(boardId);
  const [params, setParams] = useSearchParams();
  const [objectives, setObjectives] = useState<LinkableObjective[]>([]);
  const [editing, setEditing] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  useEffect(() => { void repo.listObjectives().then(setObjectives).catch(() => setObjectives([])); }, [repo]);

  // Back from the Gmail OAuth flow: open the scanner, or say why it failed.
  useEffect(() => {
    const gmail = params.get("gmail");
    const gmailErr = params.get("gmail_error");
    if (!gmail && !gmailErr) return;
    if (gmail === "connected") setScanOpen(true);
    else if (gmailErr) setError(`Gmail connect failed: ${gmailErr}`);
    const next = new URLSearchParams(params);
    next.delete("gmail"); next.delete("gmail_error");
    setParams(next, { replace: true });
  }, [params, setParams, setError]);

  // ── Derived ────────────────────────────────────────────────────────────
  const peopleById = useMemo(() => new Map((snap?.people ?? []).map(p => [p.id, p])), [snap?.people]);
  const members = useMemo<Person[]>(() => (snap?.memberIds ?? [])
    .map(id => peopleById.get(id)).filter((p): p is Person => !!p)
    .sort((a, b) => displayName(a).localeCompare(displayName(b))), [snap?.memberIds, peopleById]);
  const openToAll = isOpenToAllStaff(snap?.board);
  // Closed: the people on the board. Open: every active employee, so a new
  // hire can be assigned without a member row.
  const assignable = useMemo(
    () => assigneeChoices(openToAll, members, snap?.people ?? []),
    [openToAll, members, snap?.people],
  );
  const slugCtx = useMemo(() => ({
    personSlugs: personSlugs(assignable),
    labelSlugs: labelSlugs(snap?.labels ?? []),
  }), [assignable, snap?.labels]);
  const filters = useMemo(() => parseFilters(params, slugCtx), [params, slugCtx]);

  const idx = useMemo<CardIndex>(() => {
    const assigneesByCard = new Map<string, string[]>();
    for (const a of snap?.assignees ?? []) assigneesByCard.set(a.task_id, [...(assigneesByCard.get(a.task_id) ?? []), a.employee_id]);
    const labelsByCard = new Map<string, string[]>();
    for (const l of snap?.labelLinks ?? []) labelsByCard.set(l.task_id, [...(labelsByCard.get(l.task_id) ?? []), l.label_id]);
    return { assigneesByCard, labelsByCard, keyPrefix: snap?.board.key_prefix ?? null };
  }, [snap?.assignees, snap?.labelLinks, snap?.board.key_prefix]);

  const labelObjsByCard = useMemo(() => {
    const byId = new Map((snap?.labels ?? []).map(l => [l.id, l]));
    const out = new Map<string, Label[]>();
    for (const [cardId, ids] of idx.labelsByCard) out.set(cardId, ids.map(id => byId.get(id)).filter((l): l is Label => !!l));
    return out;
  }, [idx.labelsByCard, snap?.labels]);

  const filtering = isFiltering(filters);
  const pool = useMemo(() => (snap ? applyArchiveView(snap.cards, filters) : []), [snap, filters]);
  const visible = useMemo(() => applyFilters(pool, filters, idx), [pool, filters, idx]);
  const archivedCount = useMemo(() => (snap?.cards ?? []).filter(isArchived).length, [snap?.cards]);
  const views = useMemo<ColumnView[]>(() => (snap?.columns ?? []).map(column => ({
    column,
    cards: sortCards(visible.filter(c => c.column_id === column.id), filters.sort),
    total: pool.filter(c => c.column_id === column.id).length,
  })), [snap?.columns, visible, pool, filters.sort]);
  // The archive is one column of closed cards, not the whole empty board.
  const columnViews = useMemo(() => {
    if (!filters.archived) return views;
    const withCards = views.filter(v => v.cards.length > 0);
    if (withCards.length > 0) return withCards;
    const complete = views.filter(v => v.column.kind === "complete");
    return complete.length > 0 ? complete : views;
  }, [views, filters.archived]);

  const doneColumns = useMemo(() => new Set((snap?.columns ?? []).filter(c => c.is_done).map(c => c.id)), [snap?.columns]);
  const openCards = useMemo(
    () => (snap?.cards ?? []).filter(c => !doneColumns.has(c.column_id) && !isArchived(c)),
    [snap?.cards, doneColumns],
  );
  const openCountFor = (id: string) => openCards.filter(c => cardBelongsTo(c, [id], idx)).length;
  const pillCounts = useMemo(() => new Map(assignable.map(m => [m.id, openCards.filter(c => cardBelongsTo(c, [m.id], idx)).length])), [assignable, openCards, idx]);

  const openParam = params.get("card");
  const openCard = useMemo(() => {
    if (!snap || !openParam) return null;
    return snap.cards.find(c => c.id === openParam || shortId(snap.board, c).toLowerCase() === openParam.toLowerCase()) ?? null;
  }, [snap, openParam]);

  if (loading) return <Loading label="Loading board…" />;
  if (!snap) return <div className="p-10 text-center text-sm text-gray2">{error ?? "Board not found."}</div>;
  const { board } = snap;

  // ── Actions ────────────────────────────────────────────────────────────
  function setFilters(next: FilterState) {
    setParams(writeFilters(params, next, slugCtx), { replace: true });
  }

  function openCardView(card: Card | null) {
    const next = new URLSearchParams(params);
    if (card) next.set("card", shortId(board, card)); else next.delete("card");
    setParams(next);
  }

  async function createCard(columnId: string, title: string, assigneeIds: string[]) {
    if (!employee || !snap) return;
    const inCol = snap.cards.filter(c => c.column_id === columnId).map(c => Number(c.position));
    try {
      const card = await repo.createCard({
        boardId: board.id, columnId, title, createdBy: employee.id,
        position: inCol.length ? Math.max(...inCol) + 1 : 0,
      });
      setSnap(s => s && !s.cards.some(c => c.id === card.id) ? { ...s, cards: [...s.cards, card] } : s);
      if (assigneeIds.length > 0) {
        await repo.addAssignees(card.id, assigneeIds);
        setSnap(s => s && { ...s, assignees: [...s.assignees, ...assigneeIds
          .filter(id => !s.assignees.some(a => a.task_id === card.id && a.employee_id === id))
          .map(id => ({ task_id: card.id, employee_id: id }))] });
      }
      // Boards that expect a company priority open the card while the intent is fresh.
      if (board.requires_priority_link) openCardView(card);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function moveCard(card: Card, columnId: string, index: number) {
    if (!snap) return;
    let position: number;
    if (filters.sort === "manual") {
      position = positionAt(views.find(v => v.column.id === columnId)?.cards ?? [], index, card.id);
    } else {
      // A sorted column has no hand order to drop into; the card joins the end.
      if (columnId === card.column_id) return;
      const inCol = snap.cards.filter(c => c.column_id === columnId).map(c => Number(c.position));
      position = inCol.length ? Math.max(...inCol) + 1 : 0;
    }
    const dest = snap.columns.find(c => c.id === columnId);
    const clearArchive = shouldUnarchiveOnMove(card, dest);
    setSnap(s => s && {
      ...s,
      cards: s.cards.map(c => c.id === card.id ? {
        ...c,
        column_id: columnId,
        position,
        ...(clearArchive ? UNARCHIVE_PATCH : {}),
      } : c),
    });
    try {
      await repo.moveCard(card.id, columnId, position, clearArchive ? { clearArchive: true } : undefined);
    } catch (e) {
      setError((e as Error).message);
      await reload();
    }
  }

  const isAdmin = employee?.role === "admin" || employee?.role === "super_admin";
  const canManageMembers = isAdmin || canManageAccess || (!!employee && board.owner_employee_id === employee.id);
  const assigneesRecord = Object.fromEntries(idx.assigneesByCard);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/tasks" className="text-xs text-gray2 hover:text-teal-dark">← All boards</Link>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{board.name}</h1>
          {board.description && <p className="text-sm text-gray2">{board.description}</p>}
          {openToAll && (
            <p className="mt-1 text-sm text-teal-dark">Open to every active HQ employee.</p>
          )}
          <PeopleFilter
            people={assignable}
            selected={filters.people}
            compare={filters.compare}
            counts={pillCounts}
            onClick={(id, shift) => setFilters(clickPerson(filters, id, shift))}
            onShowAll={() => setFilters({ ...filters, people: [] })}
          />
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button type="button" onClick={() => setMembersOpen(true)} className="btn btn-ghost btn-sm flex items-center gap-1">
            <Users size={14} aria-hidden /> Members ({members.length})
          </button>
          {employee && board.owner_employee_id === employee.id && (
            <button type="button" onClick={() => setScanOpen(true)} className="btn btn-ghost btn-sm flex items-center gap-1"
              title="Scan your inbox for action items and add them here">
              <Mail size={14} aria-hidden /> Scan inbox
            </button>
          )}
          <button type="button" onClick={() => setEditing(true)} className="btn btn-ghost btn-sm flex items-center gap-1"
            title="Edit board name, description & columns">
            <Pencil size={14} aria-hidden /> Edit
          </button>
        </div>
      </div>

      {error && <div role="alert" className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}

      <FilterBar
        filters={filters}
        labels={snap.labels}
        filtering={filtering}
        matchCount={filtering ? visible.length : null}
        onQuery={q => setFilters({ ...filters, q })}
        onLabel={id => setFilters(clickLabel(filters, id))}
        onSort={sort => setFilters({ ...filters, sort })}
        onCompare={on => setFilters(setCompare(filters, on))}
        archivedCount={archivedCount}
        onToggleArchived={() => setFilters({ ...filters, archived: !filters.archived })}
        onClear={() => setFilters(clearFilters(filters))}
      />

      {filters.archived && (
        <p className="text-sm text-gray2">
          Archived cards are off the board. Open one to read it, or put it back. Nothing here is deleted.
        </p>
      )}

      <ul aria-label="Priority legend" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray2">
        {LEGEND.map(l => (
          <li key={l.label} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${l.dot}`} aria-hidden />{l.label}
          </li>
        ))}
      </ul>

      <BoardColumns
        board={board}
        views={columnViews}
        filtering={filtering}
        allowCreate={!filters.archived}
        peopleById={peopleById}
        assigneesByCard={idx.assigneesByCard}
        labelsByCard={labelObjsByCard}
        onOpen={card => openCardView(card)}
        members={assignable}
        selectedPeople={filters.people}
        onCreate={(col, title, assigneeIds) => void createCard(col, title, assigneeIds)}
        onMove={(card, col, i) => void moveCard(card, col, i)}
      />

      {openCard && (
        <TaskDetailModal
          taskId={openCard.id}
          employees={snap.people.map(p => ({ id: p.id, first_name: p.first_name, last_name: p.last_name, email: p.email ?? "" }))}
          boardMemberIds={openerIds(openToAll, snap.memberIds, snap.people)}
          assigneesByTask={assigneesRecord}
          columns={snap.columns}
          board={board}
          objectives={objectives}
          onClose={() => openCardView(null)}
          onChanged={reload}
        />
      )}

      {membersOpen && (
        <MembersPanel
          members={members}
          everyone={snap.people}
          openToAll={openToAll}
          ownerId={board.owner_employee_id}
          openCardsFor={openCountFor}
          canManage={canManageMembers}
          onAdd={async id => { await repo.addMember(board.id, id); await reload(); }}
          onRemove={async id => { const n = await repo.removeMember(board.id, id); await reload(); return n; }}
          onClose={() => setMembersOpen(false)}
        />
      )}

      {editing && (
        <BoardEditModal
          board={board}
          canToggleOpen={isAdmin || (!!employee && board.owner_employee_id === employee.id)}
          columns={snap.columns}
          tasks={snap.cards}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await reload(); }}
        />
      )}

      {scanOpen && (
        <ScanInboxModal
          boardId={board.id}
          onClose={() => setScanOpen(false)}
          onCommitted={async () => { setScanOpen(false); await reload(); }}
        />
      )}
    </div>
  );
}
